import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { trackArticleBottomAnchorReplyFinishEvent } from '@libs/eventTracking/article/trackArticleBottomAnchorEvent'
import { trackArticleCommentReplyFinishEvent } from '@libs/eventTracking/article/trackArticleCommentEvent'
import getDisplayName from '@hocs/getDisplayName'
import { Article } from '@app-core/eventTracking/article/Article'
import {
  NavigationEvents,
  OptionMenuUIEvents,
  ReplyFinishEventDetail,
  REPLY_CLOSE,
  REPLY_FINISH,
  REPLY_OPEN,
  SharedEventDetail,
} from '@libs/customEvents'
import {
  trackArticleViewEvent,
  trackArticleCopyLinkEvent,
  ShareToEnum,
  trackArticleShareEvent,
  trackArticleReportEvent,
  trackArticleDeleteEvent,
  trackArticleEnterEditorEvent,
} from '@libs/eventTracking/article'
import { useTrackArticleAuthorInfoEvent } from '@features/post/hooks/useTrackArticleAuthorInfoEvent'
import { useTrackArticleInfoEvent } from '@features/post/hooks/useTrackArticleInfoEvent'
import { getUrlSearchParams } from '@libs/url-utils'
import { useRNApp } from '@libs/RNApp'

export function withArticleEventTracking(WrappedComponent: React.ComponentType) {
  function WithArticleEventTracking(props) {
    const article = useMemo(() => props.post.getIn(['post', 'data']).toJS(), [props.post])
    useTrackArticleReplyEvent(article)
    useTrackArticleViewEvent(article)
    useTrackArticleOptionMenuEvent(article)
    useTrackArticleInfoEvent(article)
    useTrackArticleAuthorInfoEvent(article)

    return <WrappedComponent {...props} />
  }

  WithArticleEventTracking.displayName = `WithArticleEventTracking(${getDisplayName(
    WrappedComponent,
  )})`

  return WithArticleEventTracking
}

export function useTrackArticleViewEvent(article?: Article) {
  const trackedRef = React.useRef(false)
  const { isApp } = useRNApp()

  React.useEffect(() => {
    const isArticleNotReady = !article
    const isTracked = trackedRef.current
    if (isArticleNotReady) return

    if (isTracked) return

    const fromPage = (getUrlSearchParams(window.location)?.fromPage ?? {}) as Record<string, any>

    if (isApp) {
      trackArticleViewEvent({
        article,
        fromPage: {
          ...fromPage,
          pageName: window?.appFromPage,
          pageComponent: window?.appFromPageComponent,
          pageUrl: window?.appFromUrl,
          region: 'Taiwan',
        },
      })
    } else {
      trackArticleViewEvent({ article, fromPage })
    }
    trackedRef.current = true
  }, [article])
}

export function useTrackArticleReplyEvent(article: Article) {
  useEffect(() => {
    if (!article) return

    const handleArticleReplyOpen = ({
      detail: { component },
    }: CustomEventInit<SharedEventDetail>) => {
      const handleArticleReplyFinish = ({ detail }: CustomEventInit<ReplyFinishEventDetail>) => {
        if (detail.status !== 'succeeded') return
        switch (component) {
          case 'bottom-anchor':
            return trackArticleBottomAnchorReplyFinishEvent(article)
          case 'comment':
            return trackArticleCommentReplyFinishEvent(article, detail.articleId)
          case 'reply':
            return trackArticleCommentReplyFinishEvent(article, detail.replyId)
        }
      }
      window.addEventListener(REPLY_FINISH, handleArticleReplyFinish, { once: true })

      /**
       * 說明：
       *  1. 留言框關閉後，要取消前一個 `reply.finish` 的 listener，才不會送出重複的事件
       *  2. 留言成功送出後，會在 `reply.finish` 之前先有 `reply.close`，所以要多一個判斷有發生 `reply.open` 才取消前一個
       */
      window.addEventListener(
        REPLY_CLOSE,
        () => {
          window.addEventListener(
            REPLY_OPEN,
            () => {
              window.removeEventListener(REPLY_FINISH, handleArticleReplyFinish)
            },
            { once: true },
          )
        },
        { once: true },
      )
    }
    window.addEventListener(REPLY_OPEN, handleArticleReplyOpen)
    return () => {
      window.removeEventListener(REPLY_OPEN, handleArticleReplyOpen)
    }
  }, [])
}

export function useTrackArticleOptionMenuEvent(article: Article) {
  useEffect(() => {
    if (!article) return
    const handler = () => {
      trackArticleCopyLinkEvent(article)
    }
    window.addEventListener(OptionMenuUIEvents.COPY_LINK, handler)
    return () => {
      window.removeEventListener(OptionMenuUIEvents.COPY_LINK, handler)
    }
  }, [article])

  useEffect(() => {
    if (!article) return
    const handler = ({ detail: { shareTo } }: CustomEventInit<{ shareTo: ShareToEnum }>) => {
      trackArticleShareEvent(article, shareTo)
    }
    window.addEventListener(OptionMenuUIEvents.SHARE, handler)
    return () => {
      window.removeEventListener(OptionMenuUIEvents.SHARE, handler)
    }
  }, [article])

  useEffect(() => {
    if (!article) return
    const handler = () => {
      trackArticleReportEvent(article)
    }
    window.addEventListener(OptionMenuUIEvents.REPORT, handler)
    return () => {
      window.removeEventListener(OptionMenuUIEvents.REPORT, handler)
    }
  }, [article])

  useEffect(() => {
    if (!article) return
    const handler = ({ detail }: CustomEventInit<{ status: string }>) => {
      if (detail?.status !== 'succeeded') return
      trackArticleDeleteEvent(article)
    }
    window.addEventListener(OptionMenuUIEvents.DELETE, handler)
    return () => {
      window.removeEventListener(OptionMenuUIEvents.DELETE, handler)
    }
  }, [article])

  useEffect(() => {
    if (!article) return
    const handler = (event: CustomEventInit<{ toPath: string }>) => {
      const {
        detail: { toPath },
      } = event
      trackArticleEnterEditorEvent(article, toPath)
    }
    window.addEventListener(NavigationEvents.ENTER_EDITOR, handler)
    return () => {
      window.removeEventListener(NavigationEvents.ENTER_EDITOR, handler)
    }
  }, [article])
}
